import type { QueryBuilder } from "@cgarciagarcia/react-query-builder";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { errorToast, toast } from "~/ui";
import { mutations, queries } from "./factories";
import type { ImportInsuranceProviderParams } from "./types";

export const useInsuranceProvidersQuery = ({
  queryBuilder,
}: {
  queryBuilder?: QueryBuilder<Record<string, string> | undefined>;
}) => {
  return useQuery(queries.list({ queryBuilder }));
};

export const useProviderMappingsQuery = (providerId: number) => {
  return useQuery(queries.byProvider(providerId));
};

type UseImportInsuranceProviderOptions = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

export const useImportInsuranceProviderMutation = (
  options?: UseImportInsuranceProviderOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ImportInsuranceProviderParams) =>
      mutations.importInsuranceProviders(params),
    onSuccess: (data) => {
      const { imported, skipped, duplicates } = data.data;
      const dupNote =
        duplicates > 0
          ? `, ${duplicates.toString()} duplicate rows ignored`
          : "";
      toast({
        type: "success",
        title: `Import complete: ${imported.toString()} imported, ${skipped.toString()} skipped${dupNote}.`,
      });
      queryClient.invalidateQueries({
        queryKey: ["insuranceProviders"],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      errorToast(error);
      options?.onError?.(error);
    },
  });
};
